<template>
  <div>
    <a-dropdown>
      <div class="user-profile-control">
        <avatar-viewer class="avatar"
                       :img='gravatar'
                       :radius='50'
                       :width='24'
                       :height='24' />
        <span class="username">{{ user.username }}</span>
      </div>
      <template #overlay>
        <a-menu :class="menuClass ? menuClass : 'flashbase-right-menu'">
          <slot></slot>
          <a-menu-item @click="showChangeAvatar">
            <template #icon>
              <UserOutlined />
            </template>
            {{ $t('miscellaneous.UserProfile.modifyAvatar') }}
          </a-menu-item>
          <a-menu-item @click="showChangePassword">
            <template #icon>
              <LockOutlined />
            </template>
            {{ $t('miscellaneous.UserProfile.modifyPassword') }}
          </a-menu-item>
          <a-menu-item @click="onSignOut">
            <template #icon>
              <LogoutOutlined />
            </template>
            {{ $t('miscellaneous.UserProfile.signOut') }}
          </a-menu-item>
        </a-menu>
      </template>
    </a-dropdown>
    <a-modal v-model:visible="changePasswordVisible"
             :title="$t('miscellaneous.UserProfile.modifyPassword')"
             :footer="null">
      <a-tabs v-model:activeKey="mode"
              class="tabs">
        <a-tab-pane key="code"
                    :tab="$t('miscellaneous.UserProfile.viaVerificationCode')">
          <iota-reset-password :disable-to-signin='true'
                               :mobile-validator='mobileValidator'
                               @success='onSuccess' />
        </a-tab-pane>
        <a-tab-pane key="password"
                    :tab="$t('miscellaneous.UserProfile.viaOriginalPassword')"
                    class="password">
          <a-form ref="changePasswordForm"
                  :model="passwordFormState"
                  :wrapper-col="{ span: 18 }"
                  :label-col="{ span: 6 }"
                  autocomplete="off">
            <a-form-item :label="$t('miscellaneous.UserProfile.originalPassword')"
                         name="password"
                         :rules="[{ required: true, validator: validatePassword }]">
              <a-input-password v-model:value="passwordFormState.password"
                                :placeholder="$t('miscellaneous.UserProfile.originalPassword')"
                                size='large'>
                <template #prefix>
                  <lock-outlined />
                </template>
              </a-input-password>
            </a-form-item>
            <a-form-item :label="$t('miscellaneous.UserProfile.newPassword')"
                         name="newPassword"
                         :rules="[{ required: true, validator: validateNewPass }]">
              <a-input-password v-model:value="passwordFormState.newPassword"
                                :placeholder="$t('miscellaneous.UserProfile.newPassword')"
                                size='large'>
                <template #prefix>
                  <lock-outlined />
                </template>
              </a-input-password>
            </a-form-item>
            <a-form-item :label="$t('miscellaneous.UserProfile.reenterNewPassword')"
                         name="repeatPassword"
                         :rules="[{ required: true, validator: validatePassRepeat }]">
              <a-input-password v-model:value="passwordFormState.repeatPassword"
                                :placeholder="$t('miscellaneous.UserProfile.reenterNewPassword')"
                                size='large'>
                <template #prefix>
                  <lock-outlined />
                </template>
              </a-input-password>
            </a-form-item>
            <a-row class="footer">
              <a-button type="primary"
                        html-type="submit"
                        size="large"
                        @click="onChangePassword">{{ $t('miscellaneous.UserProfile.resetPassword') }}</a-button>
            </a-row>
          </a-form>
        </a-tab-pane>
      </a-tabs>
    </a-modal>
    <a-modal v-model:visible="changeAvatarVisible"
             :title="$t('miscellaneous.UserProfile.modifyAvatar')"
             :footer="null"
             class="flashbase-profile-modal"
             :body-style="{ display: 'flex', justifyContent: 'center' }">
      <avatar class='avatar-viewer'
              :width='180'
              :height='180'
              :radius='90'
              :title="$t('miscellaneous.UserProfile.uploadPersonalAvatar')"
              :preview-hint="$t('miscellaneous.UserProfile.imagePreview')"
              :crop-hint="$t('miscellaneous.UserProfile.optimalSize')"
              :select-img-hint="$t('miscellaneous.UserProfile.selectImage')"
              :ok-text="$t('miscellaneous.UserProfile.confirm')"
              :cancel-text="$t('miscellaneous.UserProfile.cancel')"
              @avatar='getFile'>
        <template #controller>
          <avatar-viewer :img='gravatar'
                         :width='180'
                         :height='180'
                         :radius='90'
                         show-upload />
        </template>
      </avatar>
      <!-- <avatar-editor /> -->
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { CORE_AXIOS, CORE_USER, Http, type UserService } from '@/core'
import type { LoginPageOption, LoginPageService, Plugin } from '@/plugins'
import { PLUGIN_AUTH } from '@/plugins/builtin/BuiltinPluginSymbol'
import { toUrl } from '@/utils'
import { Avatar, AvatarViewer } from '@iota-cn/ui-components'
import { Validator } from '@iota-cn/util-validation'
import { message } from 'ant-design-vue'
import type { Rule } from 'ant-design-vue/lib/form'
import { container } from 'tsyringe'
import { computed, reactive, ref } from 'vue'
import default_avatar from './assets/imgs/default_avatar.png'
import { useI18n } from "vue-i18n"
const { t } = useI18n()
defineProps(['menuClass'])

const axios = container.resolve<Http>(CORE_AXIOS)
const userService = container.resolve<UserService>(CORE_USER)
const user = userService.getUser()

const auth = container.resolve<Plugin<LoginPageOption, LoginPageService>>(PLUGIN_AUTH)

const mode = ref('code')

const gravatar = computed(() => {
  return toUrl(user.value.gravatar) || default_avatar
})

const onSignOut = () => {
  // 这里的接口是固定的
  auth.service.signOut('/v1/api/signout', {})
}

// 修改密码
const changePasswordForm = ref()
const changePasswordVisible = ref(false)
const showChangePassword = () => {
  changePasswordVisible.value = true
}
const passwordFormState = reactive({
  password: '',
  newPassword: '',
  repeatPassword: ''
})

const onSuccess = () => {
  changePasswordVisible.value = false
  message.success(t('miscellaneous.UserProfile.pwdModifySuccess'))
}

const onChangePassword = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  changePasswordForm.value.validateFields().then((values: any) => {
    delete values.repeatPassword
    axios.post('/v1/api/accounts/password/change', values).then(() => {
      message.success(t('miscellaneous.UserProfile.pwdModifySuccess'))
      passwordFormState.password = ''
      passwordFormState.newPassword = ''
      passwordFormState.repeatPassword = ''
      changePasswordVisible.value = false
    }).catch(() => {
      message.error(t('miscellaneous.UserProfile.pwdModifyTryLater'))
    })
  }).catch(() => {

  })
}
const validatePassword = async (_rule: Rule, value: string) => {
  if (Validator.isPassword(value)) {
    return Promise.resolve()
  } else {
    return Promise.reject(t('miscellaneous.UserProfile.enterMsg'))
  }
}
const validateNewPass = async (_rule: Rule, value: string) => {
  if (Validator.isPassword(value)) {
    if (value !== passwordFormState.repeatPassword) {
      return Promise.reject(t('miscellaneous.UserProfile.pwdNotMatch'))
    } else if (value === passwordFormState.password) {
      return Promise.reject(t('miscellaneous.UserProfile.oldAndNewSame'))
    } else {
      changePasswordForm.value.clearValidate('repeatPassword')
      return Promise.resolve()
    }
  } else {
    return Promise.reject(t('miscellaneous.UserProfile.enterMsgPwd'))
  }
}
const validatePassRepeat = async (_rule: Rule, value: string) => {
  if (Validator.isPassword(value)) {
    if (value !== passwordFormState.newPassword) {
      return Promise.reject(t('miscellaneous.UserProfile.pwdNotMatch'))
    } else if (value === passwordFormState.password) {
      changePasswordForm.value.clearValidate('newPassword')
      return Promise.reject(t('miscellaneous.UserProfile.oldAndNewSame'))
    }
    else {
      changePasswordForm.value.clearValidate('newPassword')
      return Promise.resolve()
    }
  } else {
    return Promise.reject(t('miscellaneous.UserProfile.enterMsgPwd'))
  }
}
const mobileValidator = async (_rule: Rule, value: string) => {
  // @Todo check this by server side.
  if (!user.value.mobile) {
    return Promise.reject(t('miscellaneous.UserProfile.mobileMsg'))
  }
  else if (user.value.mobile !== value) {
    return Promise.reject(t('miscellaneous.UserProfile.useMobileNumber'))
  }
  else {
    return Promise.resolve()
  }
}

// 修改头像
const changeAvatarVisible = ref(false)
const showChangeAvatar = () => {
  changeAvatarVisible.value = true
}

const getFile = (canvas: HTMLCanvasElement) => {
  canvas.toBlob((blob: Blob | null) => {
    if (blob) {
      const fd = new FormData()
      let gravatar: string
      fd.append("fileBlob", blob, `${user.value.username}.png`) // 目前后端做的验证必须加后缀
      axios.post('/v1/api/attachment/gravatar', fd)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res: any) => {
          gravatar = res.uploaded
          return axios.put('/v1/api/accounts/profile', {
            gravatar
          })
        }).then(() => {
          userService.setUser({ user: { gravatar } })
          changeAvatarVisible.value = false
        }).catch(() => {

        })
    }
  })
}

</script>

<style scoped lang="less">
.user-profile-control {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-family: 'Microsoft YaHei', '微软雅黑', Helvetica, 'PingFangSC', Arial, sans-serif;
  font-size: 14px;
  color: white;

  .avatar {
    border-radius: 50%;
    flex: 0 0 auto;
    margin-right: 10px;
  }

  .username {
    min-width: 100px;
    flex: 0 0 auto;
    width: 60px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

}

.password {
  .footer {
    button {
      width: 100%;
    }
  }
}
</style>

<style lang="less" >
.flashbase-right-menu {
  background-color: black;
  color: white;

  .ant-dropdown-menu {
    background-color: black;
    color: white;
  }

  .ant-dropdown-menu-item {
    background-color: black;
    color: white;

    &:hover {
      background-color: rgb(27, 27, 27);
    }
  }
}
</style>

<style type="less">
.flashbase-profile-modal {
  .iota-avatar-viewer {
    width: 180px !important;
    height: 180px !important;
  }
}
</style>